/**
 * Module - Base class for reusable UI components with template rendering
 *
 * Extends Controller with template loading and rendering capabilities. Foundation for
 * all rendered components (View, Page, Overlay, Input).
 *
 * KEY DISTINCTION (Module vs Widget):
 * - **Module:** Has templates and rendering logic, OWNS this.container markup
 * - **Widget:** Augments existing markup, NO rendering logic, coordinates children
 *
 * ARCHITECTURE: Fourth level in inheritance (Ancestor → Class → Controller → Module)
 * Adds template integration via Templates singleton.
 *
 * FEATURES:
 * - Automatic template loading (setup.loadTemplates)
 * - Automatic stylesheet loading (setup.loadStylesheet)
 * - Automatic show/hide with transitions
 * - Child component management
 * - Lazy loading support
 * - Tooltip support (via data-tooltip-text attributes)
 *
 * LIFECYCLE ENHANCEMENTS:
 * - Automatic hide during construction (prevents flash of unstyled content)
 * - Automatic show after construction complete
 * - Template preloading
 * - Child creation (immediate or lazy)
 *
 * @class Module
 * @extends Controller
 *
 * @param {Object} setup - Configuration object
 * @param {boolean} [setup.block=true] - Block UI during construction
 * @param {boolean} [setup.hide=true] - Hide during construction
 * @param {boolean} [setup.show=true] - Show after construction complete
 * @param {string} [setup.transition='none'] - Transition when showing
 * @param {number} [setup.duration=200] - Transition duration in ms
 * @param {boolean} [setup.loadTemplates=true] - Load Handlebars templates
 * @param {boolean} [setup.loadStylesheet=true] - Load component stylesheet
 * @param {boolean} [setup.render=false] - Render template before constructor
 * @param {boolean} [setup.prerendered=false] - Skip render if markup already exists
 * @param {Array} [setup.children] - Child components to create
 *
 * @example
 * // Create reusable module
 * const MyModule = Module.inherit({
 *     namespace: 'Modules/MyModule',
 *     defaults: {
 *         template: 'Modules/MyModule'
 *     },
 *     constructor(setup) {
 *         this.on('click', '.button', this.handleClick);
 *     }
 * });
 */
define("Module", [`Controller`, `module`], function (Super, Self) {
	return Super.inherit({
		namespace: Self.id,
		requires: [`App`, `Insights`, `Templates`, `Time`],
		defaults: {
			block: true,
			loadStylesheet: true,
			loadTemplates: true,
			render: false,
			hide: true,
			show: true,
			interceptEvents: true,
			transition: `none`,
			duration: 200,
		},
		constructor(setup) {
			const { DOM, container } = this;
			const { children, prerendered, render } = setup;
			const promises = [];

			this.addClass(`Module`); // make sure the container has the right class for styling purposes

			if (container.innerHTML === ``) {
				const { hide } = setup;

				if (hide) {
					DOM.hide({
						transition: `none`,
						done: true,
					});
				}
			}

			if (render && prerendered !== true) {
				this.render();
			}

			this.each(children, (config) => {
				// handle non-lazy "children" handling
				if (config.lazy !== true) {
					// default: false
					if (config.done === true) {
						// default: false
						this.create(config);
					} else {
						const promise = this.create(config);
						promises.push(promise);
					}
				}
			});

			this.pushConstructor(() => {
				return this.when({
					promises,
					then: () => {
						if (setup.show) {
							return DOM.show({
								transition: setup.transition,
								duration: setup.duration,
							});
						}
					},
				});
			});
		},

		ready(setup) {
			const { children } = setup;

			this.each(children, (config) => {
				// handle lazy "children" handling
				if (config.lazy === true) {
					// default: false
					this.lazyCreate(config);
				}
			});
		},

		listeners: [
			{
				event: `mouseenter`,
				filter: `[data-tooltip-text]`,
				handler(evt) {
					const { App } = this;
					const { currentTarget: hover } = evt;
					const message = hover.getAttribute(`data-tooltip-text`);
					let position = hover.getAttribute(`data-tooltip-position`);
					let target;

					if (this.isString(position)) {
						// Both properties were present
						target = hover;
					} else {
						target = this.DOM.get(`[data-tooltip-position]`, hover);
						if (this.isElement(target)) {
							position = target.getAttribute(`data-tooltip-position`);
						} else {
							target = hover;
							position = `top`;
						}
					}

					if (hover.clientWidth > 0 && hover.clientHeight > 0) {
						const direction = position;
						const done = true;

						App.spawnTooltip({ direction, done, hover, message, target });
					}
				},
			},
		],

		api: {
			/**
			 * @method Module#require
			 */
			require: {
				value(returned, options) {
					const _ = require(`lodash`);
					const { namespace } = this;
					const isApp = namespace === `App`;
					let key;

					if (_.isString(options)) {
						// requesting a single dependency return the result
						return returned;
					}

					_.defaults(options, {
						block: true,
					});

					if (options.block) {
						const random = Math.random();
						key = `require-${random}`;

						if (isApp) {
							this.block({ key });

							return this.when({
								promise: returned,
								fin: () => this.unblock({ key }),
							});
						} else {
							return this.require({
								block: false,
								deps: [`App`],
								then: () => {
									const { App } = this;
									App.block({ key });

									return this.when({
										promise: returned,
										fin: () => App.unblock({ key }),
									});
								},
							});
						}
					}

					return this.when({ promise: returned });
				},
			},

			/**
			 * Render markup using precompiled templates stored in the Controller's templates cache.
			 * @method Module#render
			 * @param {Object} options
			 * @param {boolean} options.append - If true, append rendered markup instead of replacing it
			 * @param {Object} options.data - Data used when rendering (defaults to this.data)
			 * @param {string} options.name - Template name to render (defaults to 'main')
			 * @param {element} options.target - Target element for rendered markup (defaults to this.container)
			 */
			render: {
				defaults: {
					name: `main`,
				},
				value(options) {
					const { Templates } = this;

					this.defaults(options, {
						namespace: this.namespace,
						target: this.container,
						data: this.data,
						append: false,
					});

					const templateData = this.clone(options.data);

					//Pre-pend the module's path (i.e. Inputs/Textbox/) to look up the template
					const template = Templates.get({
						namespace: options.namespace,
						name: options.name,
					});
					let dummyElement;
					let fragment;
					let markup;

					if (!this.isFunction(template)) {
						const path = `${options.namespace}.${options.name}`;
						throw new Error(`Template not found: ${path}`);
					}

					markup = template(templateData);

					if (this.isElement(options.target)) {
						if (options.append) {
							dummyElement = document.createElement(`div`);
							fragment = document.createDocumentFragment();
							dummyElement.innerHTML = markup;

							while (dummyElement.children.length > 0) {
								fragment.appendChild(dummyElement.children[0]);
							}

							options.target.appendChild(fragment);
						} else {
							options.target.innerHTML = markup;
						}
					}

					return markup;
				},
			},

			/**
			 * @method Module#updateElementContent
			 */
			updateElementContent: {
				value({ content, element, hide = true, show = true }) {
					if (element === undefined) {
						element = this.container;
					}

					if (this.isString(content)) {
						if (element.innerHTML !== content) {
							element.innerHTML = content;
						}

						if (show === true) {
							this.show({ target: element });
						}
					} else if (hide === true) {
						this.hide({ target: element });
					}
				},
			},

			/**
			 * @method Module#lazyCreate
			 */
			lazyCreate: {
				async: true,
				defaults: {
					buffer: 10, // pixel buffer
					done: true,
					setup: {},
				},
				value(options) {
					const { Deferred, DOM } = this;
					const { inViewport, isVisible } = DOM;
					const { setup, namespace } = options;
					const deferred = Deferred();
					const check = () => {
						const { buffer: cushion } = options;
						const isInViewport = inViewport({ cushion, target });
						const done = true;
						if (isInViewport && isVisible(target)) {
							const { reject: fail } = deferred;
							const then = (instance) => {
								deferred.resolve(instance);
								this.removeClass(`loading`, target);
							};
							this.create({ done, fail, namespace, setup, then });
						} else {
							const duration = 200;
							const then = check;
							this.defer({ done, duration, then });
						}
					};
					let target;

					if (!this.isString(options.namespace, 1)) {
						throw new Error(`Module.lazyCreate(): options.namespace<String> is required.`);
					} else if (this.isElement(options.target)) {
						target = options.target;
					} else if (this.isString(setup.name, 1)) {
						target = this.get(`[name="${setup.name}"]`);
					} else {
						throw new Error(`Module.lazyCreate(): options.target<Element> or options.setup.name<String> is required.`);
					}

					setup.container = target;
					this.addClass(`loading`, target);

					this.defer({
						then: check,
						done: true,
					});

					return deferred.promise;
				},
			},
		},

		destructor: function (options) {
			this.addClass(`dying`);
		},
	});
});

//# sourceMappingURL=Module.js.map